import { Check, Loader2, Play, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ORG_PLAY_TEST_ID as TEST_ID, ORG_RETRY_STATUSES as RETRY } from "@/components/agents/configuration-server/orgUnitState";
import { RowActionButton } from "@/components/brand/RowActionButton";
import { Button } from "@/components/ui/button";
import type { MigrationStatus } from "@/lib/org-structure-tree";

/**
 * Botão de play por unidade organizacional — um por card/linha. Nenhum
 * estado vira botão morto: mesmo `completed` continua clicável, porque o
 * consultor pode ter apagado o registro no CBC e precisa recadastrar.
 */
export function OrgUnitPlayButton({
  status,
  disabled = false,
  // Motivo do desabilitado (ex.: "sem conexão CBC") — mesmo padrão do
  // `title` que o botão "Run all" da toolbar já usa quando desabilitado.
  title,
  labeled = false,
  onRun,
}: {
  status: MigrationStatus;
  disabled?: boolean;
  title?: string;
  /** Botão com o nome por extenso (rodapé do painel de detalhes). */
  labeled?: boolean;
  onRun: () => void;
}) {
  const { t } = useTranslation();
  const busy = status === "running";
  const label = t(RETRY.has(status) ? "Retry" : "Run");
  const icon = busy ? (
    <Loader2 className="animate-spin" />
  ) : status === "completed" ? (
    <Check />
  ) : RETRY.has(status) ? (
    <RotateCw />
  ) : (
    <Play />
  );
  const run = () => {
    if (disabled || busy) return;
    onRun();
  };

  if (labeled) {
    return (
      <Button
        type="button"
        variant="navy"
        size="sm"
        title={title}
        aria-busy={busy}
        disabled={disabled || busy}
        data-testid={TEST_ID[status]}
        onClick={run}
      >
        {icon}
        {label}
      </Button>
    );
  }

  return (
    <RowActionButton
      label={label}
      title={title}
      aria-busy={busy}
      disabled={disabled || busy}
      data-testid={TEST_ID[status]}
      onClick={run}
      icon={icon}
    />
  );
}
